import React, {FC, useState} from "react"

import {RouteComponentProps, Redirect} from "react-router"
import {Col, Row} from "react-bootstrap"
import {PaymentCurrencyType, User, WalletEntityType} from "@irati/types"
import walletService from "@irati/wallet/graph/wallet"
import authentication from "@irati/auth"
import notify from "@irati/utils/notify"
import {InjectIntl, injectIntl} from "@irati/utils/lang"
import flexiPointService from "@irati/wallet/services/flexiRewards"
import RecognitionService from "@irati/recognition/services/Recognition"
import History from "@irati/utils/history"
import * as PropTypes from "prop-types"
import {PartialRoute} from "@irati/utils/history/routing"
import Card from "@irati/ui/src/Layout/Card"
import tracking from "@irati-tracking/wallet/rewardPointsWallet"
import {parseRecognition} from "@irati/recognition/apollo/mutation/Recognition/Create"
import {EMPTY_RECOGNITION} from "@irati-web/recognition/src/components/Popup/CreateRecognition/Popup"
import {waitPromise} from "@irati/utils/promise"
import YouNeedToMfa from "@irati-web/mfa/components/Error/YouNeedToMfa"
import Loading from "@irati/ui/src/Loading"
import mfaService from "@irati/auth/services/mfa"
import Container from "../styled/Container"
import DistributeRewardSummary from "../../components/AdminReward/DistributeReward/DistributeRewardSummary"
import {StepNames} from "../../enums"
import DistributeRewardStep1, {RewardValueTypes} from "../../components/AdminReward/DistributeReward/DistributeRewardStep1"
import DistributeRewardStep2 from "../../components/AdminReward/DistributeReward/DistributeRewardStep2"
import DistributeRewardStep3 from "../../components/AdminReward/DistributeReward/DistributeRewardStep3"

export interface DistributePayload {
  pointsPerUser: number,
  message: string,
  destinationUsers: User[]
}

export type DistributeOnChange = (payload: DistributePayload) => void
export type DistributeFlowChange = (val: RewardValueTypes) => void

const INITIAL_STATE = (): DistributePayload => ({
  pointsPerUser: 0,
  message: "",
  destinationUsers: []
})

interface Props extends InjectIntl, RouteComponentProps {}

const DistributeReward: FC<Props> = ({formatMessage, match}, context) => {
  const [assignReward, setAssignReward] = useState<RewardValueTypes>(RewardValueTypes.Employees)
  const [payload, setPayload] = useState(INITIAL_STATE())
  const [recognition, setRecognition] = useState(EMPTY_RECOGNITION(formatMessage))
  const [mfaLoad, setMfaLoad] = useState<Promise<any>>(mfaService.hasFlag())

  const showNotification = (text: string) => {
    notify.success({
      message: formatMessage({id: text}, {count: Math.round(payload.pointsPerUser) * payload.destinationUsers.length})
    })
  }

  const handleSubmit = () => {
    return walletService.get(authentication.user().uuid!, PaymentCurrencyType.Rp, WalletEntityType.Reward).then((wallet) => {
      const variables = {
        sourceId: wallet.uuid!,
        destinationUserIds: payload.destinationUsers.map(({uuid}) => uuid!),
        amount: parseFloat(payload.pointsPerUser) * 100,
        message: payload.message
      }

      if (assignReward === RewardValueTypes.Managers) {
        return flexiPointService.assignCredit(variables)
          .then(() => {
            History.push("/flexi-reward", context)
            showNotification("wallet.reward.admin.notification.assignedPoints")
            tracking.assignBudgetSuccess()
          }).catch((error) => {
            tracking.assignBudgetError(error)
          })
      }
      return flexiPointService.rewardEmployees(variables)
        .then(({transferIds}) => {
          if(authentication.apps("recognition")) {
            flexiPointService.getTransferOutcome(transferIds[0]).then(({status}) => {
              const poll = (): any => {
                return waitPromise(500).then(() => {
                  while (status === "PENDING") {return poll()}
                  if (status === "COMPLETE") {return RecognitionService.create(parseRecognition(recognition))}
                  else {throw Error("Transfer failed")}
                })
              }
              return poll()
            })
          }

          History.push("/flexi-reward", context)
          showNotification("wallet.reward.admin.notification.sendReward")
          tracking.rewardEmployeesSuccess()
        }).catch((error) => {
          tracking.rewardEmployeesError(error)
        })
    })
  }

  const handleMFARetry = async() => {
    setMfaLoad(mfaService.hasFlag())
  }

  return (
    <Loading load={mfaLoad} Error={() => <YouNeedToMfa onRetry={handleMFARetry} />}>
      <Container>
        <Row>
          <Col md={8}>
            <Card marginBottom={2}>
              <PartialRoute
                exact
                match={match.path}
                path="/"
                component={() => <Redirect to={`/wallet/flexi-assign/${StepNames.Recipients}`} />} />
              <PartialRoute
                match={match.path}
                path={`/${StepNames.Recipients}`}
                component={DistributeRewardStep1}
                componentProps={{
                  payload,
                  onPayloadChange: setPayload,
                  assignReward,
                  onAssignReward: setAssignReward,
                  recognition,
                  setRecognition
                }} />
              <PartialRoute
                match={match.path}
                path={`/${StepNames.Message}`}
                component={DistributeRewardStep2}
                componentProps={{payload, onPayloadChange: setPayload, recognition, setRecognition, assignReward}} />
              <PartialRoute
                match={match.path}
                path={`/${StepNames.Confirm}`}
                component={DistributeRewardStep3}
                componentProps={{payload, onPayloadChange: setPayload, recognition, assignReward}} />
            </Card>
          </Col>
          <Col md={4}>
            <DistributeRewardSummary
              onSubmit={handleSubmit}
              assignReward={assignReward}
              payload={payload}
              onPayloadChange={setPayload} />
          </Col>
        </Row>
      </Container>
    </Loading>
  )
}

DistributeReward.contextTypes = {
  appPath: PropTypes.string.isRequired
}

export default injectIntl(DistributeReward)
